import React, { useState, useRef, useEffect } from 'react';
import { PublicUIPlan, PlanKey, PricingDuration, PublicUILadder } from 'src/types/pricing.types';

interface PaymentDebugProps {
  plans: PublicUIPlan[];
  selectedDuration: PricingDuration;
  onDurationChange: (duration: PricingDuration) => void;
  onBoostChange: (planKey: PlanKey, type: 'increment' | 'decrement') => void;
  onCreditChange: (planKey: PlanKey, type: 'increment' | 'decrement') => void;
  onCursorUpdate: (planKey: PlanKey, ladderIndex: number) => void;
  isVisible: boolean;
  onClose: () => void;
}

const PaymentDebugUI: React.FC<PaymentDebugProps> = ({
  plans,
  selectedDuration,
  onDurationChange,
  onBoostChange,
  onCreditChange,
  onCursorUpdate,
  isVisible,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"plans">("plans");
  const currentLadderRef = useRef<HTMLDivElement>(null);

  const [selectedPlanId, setSelectedPlanId] = useState(
    plans.length > 0 ? plans[1]?.id || plans[0]?.id : "plus"
  );

  const selectedPlan = plans.find((p: any) => p.id === selectedPlanId) || plans[0];

  // Auto-scroll to current ladder when cursor changes
  useEffect(() => {
    if (currentLadderRef.current && isVisible) {
      currentLadderRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [
    selectedPlan?.cursor,
    isVisible,
    selectedPlanId,
    activeTab,
  ]);

  if (!selectedPlan || !isVisible) {
    return null;
  }

  const renderPlansTab = () => (
    <>
      {/* Duration Controls */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        <button
          onClick={() => {
            const newDuration = selectedDuration === PricingDuration.MONTHLY
              ? PricingDuration.YEARLY
              : PricingDuration.MONTHLY;
            onDurationChange(newDuration);
          }}
          style={{
            backgroundColor: "#9966cc",
            color: "white",
            padding: "4px 8px",
            fontSize: "12px",
            fontWeight: "bold",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          {selectedDuration === PricingDuration.MONTHLY
            ? "Switch to Yearly"
            : "Switch to Monthly"}
        </button>
      </div>

      {/* Plan Selection */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        <span style={{ color: "#fff", fontSize: "12px", marginRight: "8px" }}>
          Plan:
        </span>
        {plans
          .filter((planOption) => planOption.id !== "free")
          .sort((a, b) => {
            const order = ['plus', 'max', 'generative', 'team'];
            return order.indexOf(a.id) - order.indexOf(b.id);
          })
          .map((planOption) => (
            <button
              key={planOption.id}
              onClick={() => setSelectedPlanId(planOption.id)}
              style={{
                backgroundColor:
                  selectedPlan.id === planOption.id ? "#00ff00" : "#666",
                color: selectedPlan.id === planOption.id ? "#000" : "#fff",
                padding: "4px 8px",
                fontSize: "12px",
                textTransform: "capitalize",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {planOption.name || planOption.id}
            </button>
          ))}
      </div>

      {/* Navigation Controls */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        <button
          onClick={() => onBoostChange(selectedPlan.id, "decrement")}
          disabled={!selectedPlan.ladders[selectedPlan.cursor]?.prevBoost}
          style={{
            backgroundColor: "#0066cc",
            color: "white",
            padding: "4px 8px",
            fontSize: "12px",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            opacity: !selectedPlan.ladders[selectedPlan.cursor]?.prevBoost ? 0.5 : 1,
          }}
        >
          ← Boost
        </button>
        <button
          onClick={() => onBoostChange(selectedPlan.id, "increment")}
          disabled={!selectedPlan.ladders[selectedPlan.cursor]?.nextBoost}
          style={{
            backgroundColor: "#0066cc",
            color: "white",
            padding: "4px 8px",
            fontSize: "12px",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            opacity: !selectedPlan.ladders[selectedPlan.cursor]?.nextBoost ? 0.5 : 1,
          }}
        >
          Boost →
        </button>
        <button
          onClick={() => onCreditChange(selectedPlan.id, "decrement")}
          disabled={!selectedPlan.ladders[selectedPlan.cursor]?.prevCredit}
          style={{
            backgroundColor: "#cc6600",
            color: "white",
            padding: "4px 8px",
            fontSize: "12px",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            opacity: !selectedPlan.ladders[selectedPlan.cursor]?.prevCredit ? 0.5 : 1,
          }}
        >
          ← Credit
        </button>
        <button
          onClick={() => onCreditChange(selectedPlan.id, "increment")}
          disabled={!selectedPlan.ladders[selectedPlan.cursor]?.nextCredit}
          style={{
            backgroundColor: "#cc6600",
            color: "white",
            padding: "4px 8px",
            fontSize: "12px",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            opacity: !selectedPlan.ladders[selectedPlan.cursor]?.nextCredit ? 0.5 : 1,
          }}
        >
          Credit →
        </button>
      </div>

      {/* Current State */}
      <div style={{ marginBottom: "12px" }}>
        <div style={{ color: "#fff", fontWeight: "bold", marginBottom: "4px" }}>
          Current State:
        </div>
        <div style={{ color: "#ccc", fontSize: "12px" }}>
          <div>Cursor: {selectedPlan.cursor} / {selectedPlan.ladders.length - 1}</div>
          <div>Duration: {selectedDuration}</div>
          <div>Boost: {selectedPlan.ladders[selectedPlan.cursor]?.boostMultiplier || 0}x</div>
          <div>Credits: {selectedPlan.ladders[selectedPlan.cursor]?.addOnMultiplier || 0}x</div>
          <div>
            Current Price: $
            {selectedDuration === PricingDuration.MONTHLY
              ? selectedPlan.ladders[selectedPlan.cursor]?.totalMonthlyPrice || 0
              : selectedPlan.ladders[selectedPlan.cursor]?.totalYearlyPrice || 0}{" "}
            / {selectedDuration === PricingDuration.MONTHLY ? "month" : "year"}
          </div>
          <div style={{ color: "#888", fontSize: "10px" }}>
            Monthly: $
            {selectedPlan.ladders[selectedPlan.cursor]?.totalMonthlyPrice || 0} |
            Yearly: $
            {selectedPlan.ladders[selectedPlan.cursor]?.totalYearlyPrice || 0}
          </div>
        </div>
      </div>

      {/* All Ladders */}
      <div style={{ marginBottom: "12px" }}>
        <div style={{ color: "#fff", fontWeight: "bold", marginBottom: "8px" }}>
          All Ladders ({selectedPlan.ladders.length}):
        </div>
        <div style={{ maxHeight: "300px", overflowY: "auto" }}>
          {selectedPlan.ladders.map((ladder: PublicUILadder, index: number) => (
            <div
              key={index}
              ref={index === selectedPlan.cursor ? currentLadderRef : null}
              style={{
                padding: "8px",
                margin: "4px 0",
                backgroundColor: index === selectedPlan.cursor ? "#003d00" : "#2a2a2a",
                borderRadius: "4px",
                border: index === selectedPlan.cursor ? "2px solid #00ff00" : "1px solid #444",
                cursor: "pointer",
              }}
              onClick={() => {
                onCursorUpdate(selectedPlan.id, index);
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{
                  color: index === selectedPlan.cursor ? "#00ff00" : "#fff",
                  fontWeight: "bold",
                }}>
                  #{index} {index === selectedPlan.cursor ? "(CURRENT)" : ""}
                </span>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "#ccc", fontWeight: "bold" }}>
                    $
                    {selectedDuration === PricingDuration.MONTHLY
                      ? ladder.totalMonthlyPrice
                      : ladder.totalYearlyPrice}
                    /month
                  </div>
                  <div style={{ color: "#ccc", fontWeight: "bold" }}>
                    $
                    {selectedDuration === PricingDuration.MONTHLY
                      ? ladder.monthlyCostPerCredit
                      : ladder.yearlyCostPerCredit}
                    /credit
                  </div>
                </div>
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
                fontSize: "10px",
              }}>
                <div style={{ color: "#aaa" }}>
                  Boost: {ladder.boostMultiplier}x
                </div>
                <div style={{ color: "#aaa" }}>
                  Credit: {ladder.addOnMultiplier}x
                </div>
                <div style={{ color: "#aaa" }}>
                  Prev Boost: {ladder.prevBoost ? "✓" : "✗"}
                </div>
                <div style={{ color: "#aaa" }}>
                  Next Boost: {ladder.nextBoost ? "✓" : "✗"}
                </div>
                <div style={{ color: "#aaa" }}>
                  Prev Credit: {ladder.prevCredit ? "✓" : "✗"}
                </div>
                <div style={{ color: "#aaa" }}>
                  Next Credit: {ladder.nextCredit ? "✓" : "✗"}
                </div>
              </div>
              <div style={{
                color: "#888",
                fontSize: "9px",
                marginTop: "4px",
              }}>
                Addon: {ladder.addon?.id} ($
                {selectedDuration === PricingDuration.MONTHLY
                  ? ladder.addon?.monthly.amount
                  : ladder.addon?.yearly.amount}
                /{selectedDuration === PricingDuration.MONTHLY ? "mo" : "yr"}) |
                Total: $
                {selectedDuration === PricingDuration.MONTHLY
                  ? ladder.totalMonthlyPrice
                  : ladder.totalYearlyPrice}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Jump */}
      <div>
        <div style={{ color: "#fff", fontWeight: "bold", marginBottom: "4px" }}>
          Quick Jump:
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {selectedPlan.ladders.map((_: any, index: number) => (
            <button
              key={index}
              onClick={() => {
                onCursorUpdate(selectedPlan.id, index);
              }}
              style={{
                backgroundColor:
                  index === selectedPlan.cursor
                    ? "#00ff00"
                    : "#666",
                color: index === selectedPlan.cursor ? "#000" : "#fff",
                padding: "2px 6px",
                fontSize: "10px",
                minWidth: "24px",
                border: "none",
                borderRadius: "2px",
                cursor: "pointer",
              }}
            >
              {index}
            </button>
          ))}
        </div>
      </div>
    </>
  );


  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999999,
    }}>
      <div style={{
        width: "500px",
        maxHeight: "90vh",
        backgroundColor: "#1a1a1a",
        border: "2px solid #333",
        color: "#fff",
        borderRadius: "8px",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
          paddingBottom: "8px",
          borderBottom: "1px solid #444",
          padding: "16px 16px 8px 16px",
        }}>
          <div style={{ color: "#fff", fontWeight: "bold" }}>
            Payment Debug
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: "20px",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>


        {/* Content */}
        <div style={{
          width: "100%",
          maxHeight: "80vh",
          overflowY: "auto",
          backgroundColor: "#1a1a1a",
          borderRadius: "8px",
          padding: "16px",
          fontSize: "12px",
          fontFamily: "monospace",
        }}>
          {renderPlansTab()}
        </div>
      </div>
    </div>
  );
};

export default PaymentDebugUI;